import React, { useState } from 'react';
import { Grid, Box, CircularProgress, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Button, Input } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { ErrorHeader } from '../shared/errorHeader';
import { BasicCard } from '../shared/basicCard';
import { useApiCallWithAuth } from '../hooks/useApiCallWithAuth';
import { cornholeScoringApi, fortyYardDashApi, getNameFromImageApi, pitchSpeedApi } from '../api/imageApi';
import { convertBlobToBase64Url } from '../utils/binaryHelper';
import { TextResponse } from '../api/models/common';

enum ProcessingType {
	CornHoleScoring = 'cornhole',
	FortyYardDash = 'fortyyarddash',
	GetNameFromImage = 'getname',
	PitchSpeed = 'pitchspeed',
}

export const AiVisionUploadPage: React.FC = (): JSX.Element => {
	const [selectedType, setSelectedType] = useState<ProcessingType>(ProcessingType.CornHoleScoring);
	const [uploadedFile, setUploadedFile] = useState<File | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [result, setResult] = useState<TextResponse | undefined>(undefined);

	const { isAuthenticated } = useAuth0();

	const cornholeRequest = useApiCallWithAuth(cornholeScoringApi);
	const dashRequest = useApiCallWithAuth(fortyYardDashApi);
	const getNameRequest = useApiCallWithAuth(getNameFromImageApi);
	const pitchSpeedRequest = useApiCallWithAuth(pitchSpeedApi);

	const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedType(event.target.value as ProcessingType);
		setResult(undefined);
		setErrorMessage(undefined);
	};

	const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
		try {
			const file = event.target.files?.[0];
			if (file) {
				setUploadedFile(file);
				setResult(undefined);
				setErrorMessage(undefined);
			}
			event.target.value = '';
		} catch (error: any) {
			setErrorMessage(error.message || 'Error selecting file');
		}
	};

	const handleUpload = async () => {
		if (!uploadedFile) {
			setErrorMessage('Please select a file first');
			return;
		}

		setIsLoading(true);
		setErrorMessage(undefined);
		setResult(undefined);

		try {
			const fileAsString = (await convertBlobToBase64Url(uploadedFile)) as string;
			const fileCommand = { image: fileAsString };

			let response: any | undefined;

			switch (selectedType) {
				case ProcessingType.CornHoleScoring:
					response = await cornholeRequest.request(fileCommand);
					break;
				case ProcessingType.FortyYardDash:
					response = await dashRequest.request(uploadedFile);
					break;
				case ProcessingType.GetNameFromImage:
					response = await getNameRequest.request(fileCommand);
					break;
				case ProcessingType.PitchSpeed:
					response = await pitchSpeedRequest.request(uploadedFile);
					break;
			}

			if (response) {
				setResult({ text: JSON.stringify(response, null, 2) });
			} else {
				setErrorMessage('No response received from API');
			}
		} catch (error: any) {
			setErrorMessage(error.message || 'Error processing file');
		}

		setIsLoading(false);
	};

	const getAcceptedFileTypes = (): string => {
		switch (selectedType) {
			case ProcessingType.CornHoleScoring:
				return 'video/*,image/*,.zip';
			case ProcessingType.FortyYardDash:
				return 'video/*,image/*,.zip';
			case ProcessingType.PitchSpeed:
				return 'video/*,image/*,.zip';
			case ProcessingType.GetNameFromImage:
				return 'image/*';
			default:
				return '*';
		}
	};

	const renderFileUploadSection = () => {
		return (
			<BasicCard>
				<Grid container spacing={2} direction="column">
					<Grid item>
						<FormControl component="fieldset">
							<FormLabel component="legend">Processing Type</FormLabel>
							<RadioGroup value={selectedType} onChange={handleTypeChange}>
								<FormControlLabel
									value={ProcessingType.CornHoleScoring}
									control={<Radio />}
									label="CornHole Scoring"
								/>
								<FormControlLabel
									value={ProcessingType.FortyYardDash}
									control={<Radio />}
									label="40 Yard Dash Evaluation"
								/>
									<FormControlLabel
									value={ProcessingType.PitchSpeed}
									control={<Radio />}
									label="Pitch Speed Evaluation"
								/>
								<FormControlLabel
									value={ProcessingType.GetNameFromImage}
									control={<Radio />}
									label="Get Name from Image"
								/>
							</RadioGroup>
						</FormControl>
					</Grid>

					<Grid item>
						<label htmlFor="file-upload">
							<Input
								id="file-upload"
								sx={{ display: 'none' }}
								inputProps={{ accept: getAcceptedFileTypes() }}
								type="file"
								onChange={handleFileSelect}
							/>
							<Button color="secondary" variant="contained" component="span">
								Select File
							</Button>
						</label>
						{uploadedFile && (
							<Box mt={1}>
								<span style={{ fontSize: 'medium' }}>
									Selected: {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
								</span>
							</Box>
						)}
					</Grid>

					<Grid item>
						{!isLoading && uploadedFile && (
							<Button color="primary" variant="contained" onClick={handleUpload}>
								Upload and Process
							</Button>
						)}
						{isLoading && <CircularProgress />}
					</Grid>
				</Grid>
			</BasicCard>
		);
	};

	const renderResultsSection = () => {
		if (!result) {
			return null;
		}

		return (
			<BasicCard>
				<Grid container spacing={2} direction="column">
					<Grid item>
						<h3>Results</h3>
					</Grid>
					<Grid item>
						<Box
							sx={{
								padding: 2,
								backgroundColor: '#f5f5f5',
								borderRadius: 1,
								border: '1px solid #ddd',
								whiteSpace: 'pre-wrap',
								fontFamily: 'monospace',
							}}
						>
							{result.text}
						</Box>
					</Grid>
				</Grid>
			</BasicCard>
		);
	};

	return (
		<Box display="flex" justifyContent="center" alignItems="flex-start" minHeight="100vh" px={2}>
			<Box width="100%" maxWidth="800px">
				<Grid container spacing={3} direction="column">
					<Grid item>
						<h2>AI Vision Upload</h2>
					</Grid>

					<Grid item>
						<ErrorHeader errorMessage={errorMessage} />
					</Grid>

					{isAuthenticated && (
						<>
							<Grid item>{renderFileUploadSection()}</Grid>

							<Grid item>{renderResultsSection()}</Grid>
						</>
					)}

					{!isAuthenticated && (
						<Grid item>
							<ErrorHeader errorMessage="You must be logged in to use this feature" />
						</Grid>
					)}
				</Grid>
			</Box>
		</Box>
	);
};
